import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Home, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * PracticeExitControls
 * --------------------
 * Persistent, always-visible controls kids can tap to leave a practice module
 * mid-session. Renders a floating pill (top-left by default) with an X (exit)
 * and a Pause button. ESC key also opens the exit dialog. Kids never get stuck.
 *
 * Props:
 *   childId       — used to build the home route (default `/learn/{childId}`)
 *   onPause       — optional; called BEFORE navigating away so the parent page
 *                   can persist progress
 *   onExit        — optional; overrides the default home navigation
 *   homePath      — optional; overrides the default home path
 *   className     — extra classes for positioning
 *   showPause     — default true; hide by passing false
 */
const PracticeExitControls = ({
  childId,
  onPause,
  onExit,
  homePath,
  className = '',
  showPause = true,
}) => {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const defaultHome = homePath || (childId ? `/learn/${childId}` : '/dashboard');

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setConfirmOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const goHome = () => {
    setConfirmOpen(false);
    try { onPause && onPause({ reason: 'exit' }); } catch {}
    if (onExit) {
      onExit();
    } else {
      navigate(defaultHome);
    }
  };

  const handlePause = () => {
    try { onPause && onPause({ reason: 'pause' }); } catch {}
    // Pause also returns to the hub — progress is snapshot by the caller.
    navigate(defaultHome);
  };

  return (
    <>
      <div
        className={`fixed top-3 left-3 z-[70] flex items-center gap-2 bg-white/95 backdrop-blur rounded-full shadow-xl border border-slate-200 px-2 py-1.5 ${className}`}
        data-testid="practice-exit-controls"
      >
        <button
          onClick={() => setConfirmOpen(true)}
          aria-label="Exit practice"
          data-testid="practice-exit-btn"
          className="w-10 h-10 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md transition-all hover:scale-105"
        >
          <X className="w-5 h-5" />
        </button>
        {showPause && (
          <button
            onClick={handlePause}
            aria-label="Pause and go home"
            data-testid="practice-pause-btn"
            className="w-10 h-10 rounded-full bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center shadow-md transition-all hover:scale-105"
          >
            <Pause className="w-5 h-5" />
          </button>
        )}
        <button
          onClick={() => navigate(defaultHome)}
          aria-label="Home"
          data-testid="practice-home-btn"
          className="w-10 h-10 rounded-full bg-teal-600 hover:bg-teal-700 text-white flex items-center justify-center shadow-md transition-all hover:scale-105"
        >
          <Home className="w-5 h-5" />
        </button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="practice-exit-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl">Leave this activity?</DialogTitle>
            <DialogDescription>
              Your progress will be saved. You can come back and finish anytime.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="practice-exit-cancel">
              Keep going
            </Button>
            <Button
              onClick={goHome}
              className="bg-red-500 hover:bg-red-600"
              data-testid="practice-exit-confirm"
            >
              Yes, leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PracticeExitControls;
